// Helpers of the Logs tab (components/AppLogsPanel.vue): time ranges, level tones, line identity and text.
import type { AppLogLine } from '../api/types';

export const RANGES = {
  '15m': { label: '15 min', text: 'last 15 minutes', ms: 15 * 60_000 },
  '1h': { label: '1 h', text: 'last hour', ms: 60 * 60_000 },
  '6h': { label: '6 h', text: 'last 6 hours', ms: 6 * 60 * 60_000 },
  '24h': { label: '24 h', text: 'last 24 hours', ms: 24 * 60 * 60_000 },
  '7d': { label: '7 days', text: 'last 7 days', ms: 7 * 24 * 60 * 60_000 },
} as const;
export type RangeKey = keyof typeof RANGES;

/** The tone of a level as logged: syslog names (err, crit, …) and Apache's `module:level`. */
export function levelTone(level: string): string {
  const l = level.toLowerCase().replace(/^[a-z_]+:/, '');
  if (/^(err|error|crit|critical|alert|emerg|emergency|fatal)$/.test(l)) return 'tone-bad';
  if (/^(warn|warning)$/.test(l)) return 'tone-warn';
  if (/^(info|notice)$/.test(l)) return 'tone-ok';
  return 'tone-idle';
}

/** Identifies a line across pages and live polls (time with nanoseconds plus the message). */
export function lineKey(l: AppLogLine): string {
  return `${l.time ?? ''}\u0000${l.app ?? ''}\u0000${l.message}`;
}

/** `2026-11-03T09:12:53.990221Z` → `2026-11-03 09:12:53.990` (UTC, milliseconds). */
export function lineTime(t?: string | null): string {
  if (!t) return '—';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?Z$/.exec(t);
  if (!m) return t;
  return `${m[1]} ${m[2]}.${(m[3] ?? '').padEnd(3, '0').slice(0, 3)}`;
}

/** One line of the downloaded file. */
export function logText(l: AppLogLine): string {
  return [l.time ?? '-', l.level ?? '-', l.app ?? '-', l.category ?? '-', l.message].join(' ');
}
